// ============================================================
// Public Pages — Features, About, Contact, Terms, Privacy, FAQs
// ============================================================

import { Link } from 'react-router-dom';
import { useState } from 'react';
import {
    Briefcase, FileText, Brain, BarChart3, Shield, Zap, Users, Clock,
    ArrowRight, Mail, Phone, MapPin, Send, ChevronDown, ChevronUp,
    Sparkles, Globe, Lock, CheckCircle2, Target, Layers
} from 'lucide-react';
import toast from 'react-hot-toast';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// ── Shared Header & Footer ────────────────────────────────────

function PublicHeader() {
    return (
        <nav style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '20px 40px', borderBottom: '1px solid var(--color-border)',
            position: 'sticky', top: 0, background: 'var(--color-bg-primary)',
            zIndex: 100, backdropFilter: 'blur(12px)',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: 800, fontSize: 18,
                    }}>H</div>
                    <span style={{
                        fontSize: '1.25rem', fontWeight: 800,
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    }}>HireHub</span>
                </Link>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Link to="/features" className="btn btn-ghost" style={{ fontSize: 14 }}>Features</Link>
                    <Link to="/about" className="btn btn-ghost" style={{ fontSize: 14 }}>About</Link>
                    <Link to="/contact" className="btn btn-ghost" style={{ fontSize: 14 }}>Contact</Link>
                </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
                <Link to="/login" className="btn btn-ghost">Sign In</Link>
                <Link to="/signup" className="btn btn-primary">Get Started</Link>
            </div>
        </nav>
    );
}

export function PublicFooter() {
    return (
        <footer style={{
            borderTop: '1px solid var(--color-border)',
            padding: '48px 40px 24px', background: 'var(--color-bg-secondary)',
        }}>
            <div style={{
                maxWidth: 1000, margin: '0 auto',
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 40, marginBottom: 40,
            }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontWeight: 800, fontSize: 16,
                        }}>H</div>
                        <span style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--color-text-primary)' }}>HireHub</span>
                    </div>
                    <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.7 }}>
                        AI-powered hiring automation platform. Streamline your recruitment process from job posting to offer.
                    </p>
                </div>
                <div>
                    <h4 style={{ fontWeight: 700, fontSize: 13, marginBottom: 16, color: 'var(--color-text-primary)' }}>Product</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <Link to="/features" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>Features</Link>
                        <Link to="/about" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>About Us</Link>
                        <Link to="/contact" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>Contact</Link>
                    </div>
                </div>
                <div>
                    <h4 style={{ fontWeight: 700, fontSize: 13, marginBottom: 16, color: 'var(--color-text-primary)' }}>Legal</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <Link to="/terms" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>Terms & Conditions</Link>
                        <Link to="/privacy" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>Privacy Policy</Link>
                        <Link to="/faqs" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>FAQs</Link>
                    </div>
                </div>
                <div>
                    <h4 style={{ fontWeight: 700, fontSize: 13, marginBottom: 16, color: 'var(--color-text-primary)' }}>Contact</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13, color: '#6b7280' }}>
                        <span><Mail size={14} style={{ display: 'inline', marginRight: 6 }} />hirehub2027@gmail.com</span>
                        <span><Phone size={14} style={{ display: 'inline', marginRight: 6 }} />+91 7428988615</span>
                    </div>
                </div>
            </div>
            <div style={{
                borderTop: '1px solid var(--color-border)', paddingTop: 20,
                textAlign: 'center', color: '#6b7280', fontSize: '12px',
            }}>
                © {new Date().getFullYear()} HireHub. All rights reserved.
            </div>
        </footer>
    );
}

function PublicLayout({ children }: { children: React.ReactNode }) {
    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--color-bg-primary)' }}>
            <PublicHeader />
            <main style={{ flex: 1 }}>{children}</main>
            <PublicFooter />
        </div>
    );
}

// ── Features Page ─────────────────────────────────────────────

const features = [
    { icon: <Briefcase size={28} />, title: 'Job Pipeline Management', desc: 'Create customizable hiring pipelines with drag-and-drop stages. Track every candidate from application to offer with a visual Kanban-style board.' },
    { icon: <FileText size={28} />, title: 'Dynamic Form Builder', desc: 'Build application forms with 12+ field types including table grids, file uploads, and conditional logic. Version your forms and track changes.' },
    { icon: <Brain size={28} />, title: 'AI-Powered Scoring', desc: 'Automatic assessment grading using advanced AI models. Detailed rubric-based scoring with explanations and confidence indicators.' },
    { icon: <BarChart3 size={28} />, title: 'Leaderboards & Analytics', desc: 'Ranked candidate views with composite scores. Export data to CSV. Real-time dashboard with conversion rates and pipeline analytics.' },
    { icon: <Shield size={28} />, title: 'Secure Assessments', desc: 'Timed tests with OTP verification, question shuffling, auto-save, and anti-cheat measures. Multi-attempt support with configurable limits.' },
    { icon: <Zap size={28} />, title: 'Question Bank', desc: 'Versioned MCQ and subjective questions with difficulty tagging. Reuse questions across assessments with automatic version tracking.' },
    { icon: <Users size={28} />, title: 'Team Collaboration', desc: 'Invite team members with role-based access control. Owner, Admin, Recruiter, and Viewer roles with granular permissions.' },
    { icon: <Clock size={28} />, title: 'Automated Workflows', desc: 'Auto-stage assignment on application. Batch invite candidates for assessments. Scheduled reminders and follow-ups.' },
    { icon: <Globe size={28} />, title: 'Public Application Links', desc: 'Generate shareable links for application forms and assessments. Embed forms on your career page or share via email.' },
    { icon: <Lock size={28} />, title: 'Enterprise Security', desc: 'Full audit logging, data encryption at rest, GDPR-compliant data handling, and configurable data retention policies.' },
    { icon: <Target size={28} />, title: 'Candidate Profiles', desc: 'Rich candidate profiles with form data, assessment scores, stage history, uploaded documents, and internal notes.' },
    { icon: <Layers size={28} />, title: 'Multi-Tenant Architecture', desc: 'Isolated data per organization. Each company gets its own workspace with independent settings and team management.' },
];

export function FeaturesPage() {
    return (
        <PublicLayout>
            <section style={{ textAlign: 'center', padding: '60px 40px 40px', maxWidth: 700, margin: '0 auto' }}>
                <div style={{
                    display: 'inline-block', padding: '6px 16px', borderRadius: 20,
                    background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1',
                    fontSize: 13, fontWeight: 600, marginBottom: 20,
                }}>
                    <Sparkles size={14} style={{ display: 'inline', marginRight: 6 }} /> Platform Features
                </div>
                <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 800, lineHeight: 1.2, marginBottom: 16 }}>
                    Everything you need to{' '}
                    <span style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        hire smarter
                    </span>
                </h1>
                <p style={{ fontSize: '1rem', color: '#4b5563', lineHeight: 1.8 }}>
                    A complete hiring platform with AI scoring, pipeline management, and team collaboration — built for modern recruiters.
                </p>
            </section>
            <section style={{
                maxWidth: 1100, margin: '0 auto', padding: '0 40px 80px',
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24,
            }}>
                {features.map((f, i) => (
                    <div key={i} className="card" style={{
                        display: 'flex', flexDirection: 'column', gap: 14,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                    }}>
                        <div style={{
                            width: 56, height: 56, borderRadius: 14,
                            background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>{f.icon}</div>
                        <h3 style={{ fontWeight: 700, fontSize: 16 }}>{f.title}</h3>
                        <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7 }}>{f.desc}</p>
                    </div>
                ))}
            </section>
            <section style={{
                textAlign: 'center', padding: '60px 40px',
                background: 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(139,92,246,0.08))',
            }}>
                <h2 style={{ fontSize: '1.8rem', fontWeight: 800, marginBottom: 16 }}>Ready to transform your hiring?</h2>
                <p style={{ color: '#4b5563', marginBottom: 24, fontSize: '1rem' }}>Start for free. No credit card required.</p>
                <Link to="/signup" className="btn btn-primary btn-lg">
                    Get Started <ArrowRight size={18} />
                </Link>
            </section>
        </PublicLayout>
    );
}

// ── About Page ────────────────────────────────────────────────

export function AboutPage() {
    return (
        <PublicLayout>
            <section style={{ maxWidth: 700, margin: '0 auto', padding: '60px 40px' }}>
                <div style={{
                    display: 'inline-block', padding: '6px 16px', borderRadius: 20,
                    background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1',
                    fontSize: 13, fontWeight: 600, marginBottom: 20,
                }}>About HireHub</div>

                <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 800, lineHeight: 1.3, marginBottom: 24 }}>
                    Built to simplify{' '}
                    <span style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        hiring
                    </span>
                </h1>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 20, color: '#374151', fontSize: '15px', lineHeight: 1.8 }}>
                    <p>
                        HireHub was created by <strong style={{ color: 'var(--color-text-primary)' }}>Deepanshu Goyal</strong> with
                        a vision to eliminate the chaos of traditional hiring. We believe every company — from startups to enterprises — deserves
                        a powerful, AI-driven hiring platform that's both easy to use and deeply capable.
                    </p>
                    <p>
                        We saw how recruiters struggled with fragmented tools — spreadsheets for tracking, separate platforms for assessments,
                        manual scoring of answers, and disjointed communication. HireHub brings everything together in one unified platform.
                    </p>

                    <div className="card" style={{ padding: 32, textAlign: 'center', background: 'var(--color-bg-secondary)' }}>
                        <div style={{
                            width: 80, height: 80, borderRadius: '50%', margin: '0 auto 16px',
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontWeight: 800, fontSize: 32,
                        }}>D</div>
                        <h3 style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Deepanshu Goyal</h3>
                        <p style={{ color: '#6366f1', fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Founder & CEO</p>
                        <p style={{ fontSize: 13, color: '#4b5563', maxWidth: 400, margin: '0 auto', lineHeight: 1.7 }}>
                            Passionate about building products that solve real-world problems.
                            HireHub is the result of combining AI innovation with practical recruitment needs.
                        </p>
                    </div>

                    <h2 style={{ fontWeight: 700, fontSize: '1.4rem', color: 'var(--color-text-primary)', marginTop: 12 }}>Our Mission</h2>
                    <p>
                        To democratize smart hiring. We want every recruiter to have access to AI-powered tools
                        that save time, reduce bias, and help find the best talent — without breaking the bank.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 8 }}>
                        {[
                            { num: '500+', label: 'Assessments Created' },
                            { num: '1000+', label: 'Candidates Evaluated' },
                            { num: '95%', label: 'Time Saved on Scoring' },
                            { num: '24/7', label: 'Platform Availability' },
                        ].map((s, i) => (
                            <div key={i} className="card" style={{ textAlign: 'center', padding: 20 }}>
                                <div style={{
                                    fontSize: 28, fontWeight: 800, marginBottom: 4,
                                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                }}>{s.num}</div>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}

// ── Contact Page ──────────────────────────────────────────────

export function ContactPage() {
    const [form, setForm] = useState({ name: '', email: '', message: '' });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error('Please fill all fields');
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API_BASE}/public/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            if (res.ok) {
                toast.success('Message sent! We\'ll get back to you soon.');
                setForm({ name: '', email: '', message: '' });
            } else {
                toast.error('Failed to send. Please email us directly.');
            }
        } catch {
            toast.error('Failed to send. Please email us directly.');
        }
        setSubmitting(false);
    };

    return (
        <PublicLayout>
            <section style={{ maxWidth: 800, margin: '0 auto', padding: '60px 40px' }}>
                <div style={{ textAlign: 'center', marginBottom: 48 }}>
                    <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 800, marginBottom: 12 }}>
                        Get in Touch
                    </h1>
                    <p style={{ color: '#4b5563', fontSize: '1rem' }}>
                        Have questions? We'd love to hear from you.
                    </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                    {/* Contact Info */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: 12,
                                background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}><Mail size={22} /></div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 14 }}>Email</div>
                                <a href="mailto:hirehub2027@gmail.com" style={{ fontSize: 13, color: '#6366f1', textDecoration: 'none' }}>
                                    hirehub2027@gmail.com
                                </a>
                            </div>
                        </div>
                        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: 12,
                                background: 'rgba(16, 185, 129, 0.1)', color: '#10b981',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}><Phone size={22} /></div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 14 }}>Phone</div>
                                <a href="tel:+917428988615" style={{ fontSize: 13, color: '#10b981', textDecoration: 'none' }}>
                                    +91 7428988615
                                </a>
                            </div>
                        </div>
                        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: 12,
                                background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}><MapPin size={22} /></div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 14 }}>Location</div>
                                <span style={{ fontSize: 13, color: '#4b5563' }}>India</span>
                            </div>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <form onSubmit={handleSubmit} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label">Name</label>
                            <input className="form-input" value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                placeholder="Your name" />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label">Email</label>
                            <input className="form-input" type="email" value={form.email}
                                onChange={e => setForm({ ...form, email: e.target.value })}
                                placeholder="you@company.com" />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label">Message</label>
                            <textarea className="form-textarea" value={form.message}
                                onChange={e => setForm({ ...form, message: e.target.value })}
                                placeholder="Tell us how we can help..." rows={4} />
                        </div>
                        <button className="btn btn-primary" type="submit" disabled={submitting}>
                            <Send size={16} /> {submitting ? 'Sending...' : 'Send Message'}
                        </button>
                    </form>
                </div>
            </section>
        </PublicLayout>
    );
}

// ── Terms & Conditions ────────────────────────────────────────

export function TermsPage() {
    return (
        <PublicLayout>
            <section style={{ maxWidth: 700, margin: '0 auto', padding: '60px 40px' }}>
                <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: 8 }}>Terms & Conditions</h1>
                <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 32 }}>Last updated: March 2026</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24, color: '#374151', fontSize: 14, lineHeight: 1.8 }}>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>1. Acceptance of Terms</h2>
                        <p>By accessing or using HireHub ("the Platform"), you agree to be bound by these Terms and Conditions. If you do not agree, you must not use the Platform.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>2. Account Registration</h2>
                        <p>You must provide accurate information when creating an account. You are responsible for maintaining the confidentiality of your credentials and for all activities under your account.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>3. Use of the Platform</h2>
                        <p>HireHub is provided for lawful hiring and recruitment purposes only. You agree not to use the Platform to discriminate against candidates based on protected characteristics, or for any illegal activities.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>4. Data Ownership</h2>
                        <p>You retain ownership of all data you upload to the Platform, including candidate information, assessment content, and form submissions. HireHub processes this data solely to provide the service.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>5. AI Scoring</h2>
                        <p>AI-generated scores are provided as decision-support tools and should not be the sole basis for hiring decisions. Human review and override capabilities are always available.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>6. Service Availability</h2>
                        <p>We strive for high availability but do not guarantee uninterrupted service. Scheduled maintenance will be communicated in advance whenever possible.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>7. Limitation of Liability</h2>
                        <p>HireHub shall not be liable for any indirect, incidental, or consequential damages arising from the use of the Platform. Our total liability is limited to the fees paid in the preceding 12 months.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>8. Termination</h2>
                        <p>Either party may terminate the agreement at any time. Upon termination, you may request an export of your data within 30 days.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>9. Contact</h2>
                        <p>For questions about these terms, contact us at <a href="mailto:hirehub2027@gmail.com" style={{ color: '#6366f1' }}>hirehub2027@gmail.com</a>.</p>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}

// ── Privacy Policy ────────────────────────────────────────────

export function PrivacyPage() {
    return (
        <PublicLayout>
            <section style={{ maxWidth: 700, margin: '0 auto', padding: '60px 40px' }}>
                <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: 8 }}>Privacy Policy</h1>
                <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 32 }}>Last updated: March 2026</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24, color: '#374151', fontSize: 14, lineHeight: 1.8 }}>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>1. Information We Collect</h2>
                        <p>We collect information you provide when registering, creating jobs, building forms, and managing assessments. For candidates, we collect information submitted through application forms and assessment responses.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>2. How We Use Your Data</h2>
                        <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <li>To provide and maintain the recruitment platform</li>
                            <li>To process and score assessments using AI models</li>
                            <li>To generate analytics and reporting for your hiring pipeline</li>
                            <li>To send essential service communications</li>
                        </ul>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>3. Data Storage & Security</h2>
                        <p>All data is stored in encrypted databases. We use industry-standard security practices including HTTPS encryption, secure authentication, and role-based access control.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>4. AI Processing</h2>
                        <p>Subjective assessment answers are processed by third-party AI providers (such as Groq, Google Gemini, or OpenRouter) for automated scoring. Only the question text and candidate answer are shared — no personal identifying information is sent to AI providers.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>5. Data Retention</h2>
                        <p>Data is retained as long as your account is active. You may request deletion of your data at any time. Configurable data retention policies are available for enterprise accounts.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>6. Third-Party Services</h2>
                        <p>We use third-party services for hosting (Render, Vercel), database (Turso), and AI scoring. Each provider has their own privacy policy and data handling practices.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>7. Your Rights</h2>
                        <p>You have the right to access, correct, or delete your personal data. You can export your data at any time through the platform's export features.</p>
                    </div>
                    <div>
                        <h2 style={{ fontWeight: 700, fontSize: 18, color: 'var(--color-text-primary)', marginBottom: 8 }}>8. Contact</h2>
                        <p>For privacy-related inquiries, contact us at <a href="mailto:hirehub2027@gmail.com" style={{ color: '#6366f1' }}>hirehub2027@gmail.com</a>.</p>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}

// ── FAQs Page ─────────────────────────────────────────────────

const faqs = [
    { q: 'What is HireHub?', a: 'HireHub is an AI-powered hiring automation platform that helps you manage your entire recruitment process — from job postings and application forms to assessments and candidate scoring — all in one place.' },
    { q: 'How does AI scoring work?', a: 'When a candidate submits subjective answers, our AI scores them using detailed rubrics you define. Each answer gets a score, explanation, and confidence rating. MCQ questions are scored automatically. You can always override AI scores.' },
    { q: 'Is HireHub free?', a: 'HireHub offers a free tier that includes all core features. Enterprise plans with advanced analytics and priority support are available for larger teams.' },
    { q: 'Can I invite my team?', a: 'Yes! Go to Settings → Team Members to invite colleagues. You can assign roles like Admin, Recruiter, or Viewer with different permission levels.' },
    { q: 'How secure is candidate data?', a: 'All data is encrypted at rest and in transit. We use role-based access control, full audit logging, and comply with data protection best practices. You can configure data retention policies.' },
    { q: 'Can I export candidate data?', a: 'Yes. You can export candidate data, assessment results, and leaderboard rankings as CSV files from any job\'s detail page.' },
    { q: 'What assessment features are available?', a: 'Timed tests, OTP verification, question shuffling, auto-save, multiple attempts, MCQ and subjective questions, file uploads, and AI-powered scoring with detailed rubrics.' },
    { q: 'Can candidates upload files?', a: 'Yes! Application forms support file uploads (resumes, photos, certificates). These are stored securely and viewable in the candidate\'s profile.' },
    { q: 'How do I contact support?', a: 'Email us at hirehub2027@gmail.com or call +91 7428988615. We typically respond within 24 hours.' },
];

function FAQItem({ q, a }: { q: string; a: string }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="card" style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                <h3 style={{ fontWeight: 600, fontSize: 15 }}>{q}</h3>
                {open ? <ChevronUp size={18} color="#6b7280" /> : <ChevronDown size={18} color="#6b7280" />}
            </div>
            {open && (
                <p style={{ marginTop: 12, fontSize: 14, color: '#4b5563', lineHeight: 1.7 }}>{a}</p>
            )}
        </div>
    );
}

export function FAQsPage() {
    return (
        <PublicLayout>
            <section style={{ maxWidth: 700, margin: '0 auto', padding: '60px 40px' }}>
                <div style={{ textAlign: 'center', marginBottom: 48 }}>
                    <h1 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 800, marginBottom: 12 }}>
                        Frequently Asked Questions
                    </h1>
                    <p style={{ color: '#4b5563', fontSize: '1rem' }}>
                        Everything you need to know about HireHub.
                    </p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {faqs.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} />)}
                </div>
                <div style={{ textAlign: 'center', marginTop: 48 }}>
                    <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 16 }}>Still have questions?</p>
                    <Link to="/contact" className="btn btn-primary">
                        <Mail size={16} /> Contact Us
                    </Link>
                </div>
            </section>
        </PublicLayout>
    );
}
